import { useLocalStorageValue } from "@react-hookz/web";
import { Controller, useForm } from "react-hook-form";
import { Button, CheckboxInput, IconButton, Typography, Modal, SortableList } from "@/components/atoms";
import { useOperator } from "@/hooks";
import { allColumns } from "../../fixtures";
import { TableColumn } from "../../types";

export interface BookingCustomizeColumnsProps {
  open: boolean;
  onClose: () => void;
}

type FormData = {
  columns: string[];
};

const selectableColumns = allColumns
  .filter((i) => !i.isFixed)
  .sort((a, b) => a.header.localeCompare(b.header));
const defaultColumns = allColumns.filter((i) => !i.isFixed && i.isDefault);

export const BookingCustomizeColumns = ({ open, onClose }: BookingCustomizeColumnsProps) => {
  const { id: operatorId } = useOperator();
  const { set: setColumns, value: savedColumns } = useLocalStorageValue(`${operatorId}:booking-columns:v2`, {
    defaultValue: defaultColumns.map((i) => i.id),
  });

  const columns = savedColumns
    ? savedColumns.map(id => allColumns.find(col => col.id === id)).filter(Boolean)
    : defaultColumns;

  const { control, setValue, watch, handleSubmit } = useForm<FormData>({
    defaultValues: { columns: columns.map((i) => i?.id) },
  });

  const selectedColumns = watch("columns");
  const listItems = selectedColumns.reduce((acc, id) => {
    const item = selectableColumns.find((i) => i.id === id);
    if (item) acc.push(item);
    return acc;
  }, [] as TableColumn[]);

  const handleSelectAllChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    if (target.checked) {
      setValue(
        "columns",
        selectableColumns.map((i) => i.id)
      );
    } else {
      setValue("columns", []);
    }
  };

  const handleSortChange = (items: TableColumn[]) => {
    setValue(
      "columns",
      items.map((i) => i.id)
    );
  };

  const handleResetClick = () => {
    setValue(
      "columns",
      defaultColumns.map((i) => i.id)
    );
  };

  const onSubmit = () => {
    setColumns(listItems.map((i) => i.id));
    onClose();
  };

  const renderItem = (item: TableColumn) => {
    return (
      <SortableList.Item id={item.id}>
        <div className="flex h-[40px] items-center gap-2 overflow-hidden rounded-lg bg-white p-4 shadow">
          <SortableList.DragHandle />
          <Typography className="flex-1">{item.header}</Typography>
          <IconButton
            onClick={() =>
              setValue(
                "columns",
                selectedColumns.filter((i) => i !== item.id)
              )
            }
            className="border-none p-1"
            iconName="close"
            isCustomIcon
            iconSize="sm"
            variant="secondary"
          />
        </div>
      </SortableList.Item>
    );
  };

  return (
    <Modal open={open} onClose={onClose} className="w-[800px] !p-6">
      <div className="flex flex-col gap-2">
        <Typography variant="title">Customize Columns</Typography>
        <Typography variant="paragraph">
          Select the columns you want to display on your booking dashboard and then drag and move them into the order you want them displayed
        </Typography>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-5">
        <div className="grid grid-cols-2">
          <div className="h-[560px] px-4 py-3 bg-neutral-surface-gray rounded-l-lg overflow-y-auto">
            <Typography variant="title">Columns</Typography>
            <div className="mt-2 flex flex-col gap-2 ">
              <CheckboxInput
                label="Select all"
                variant="check"
                onChange={handleSelectAllChange}
                checked={selectedColumns.length === selectableColumns.length}
              />
              <Controller
                control={control}
                name="columns"
                render={({ field }) => (
                  <>
                    {selectableColumns.map((i) => (
                      <div key={i.id} className="flex items-center gap-2">
                        <CheckboxInput
                          id={i.id}
                          value={i.id}
                          label={i.header}
                          checked={field.value.includes(i.id)}
                          variant="check"
                          onChange={({ target }) => {
                            if (target.checked) {
                              field.onChange([...field.value, target.value]);
                            } else {
                              field.onChange(field.value.filter((value) => value !== target.value));
                            }
                          }}
                        />
                      </div>
                    ))}
                  </>
                )}
              />
            </div>
          </div>
          <div className="h-[560px] px-4 py-3 bg-neutral-gray rounded-r-lg overflow-y-auto">
            <SortableList items={listItems} onChange={handleSortChange} renderItem={renderItem} />
          </div>
        </div>
        <div className="flex flex-row items-center justify-end mt-8 gap-x-3">
          <Button size="lg" variant="secondary" onClick={onClose}>
            Close
          </Button>
          <Button size="lg" variant="secondary" onClick={handleResetClick}>
            Reset Default
          </Button>
          <Button type="submit" size="lg">
            Apply Changes
          </Button>
        </div>
      </form>
    </Modal>
  );
};